import { login, requestPasswordReset, resetPassword } from "./api";
import {
  TOKEN_KEY,
  initialForgotPassword,
  initialLogin,
  initialResetPassword,
} from "./constants";
import { getApiErrorMessage, hasValidationErrorForField } from "./formatters";

export function useAuthActions({
  fetchDashboard,
  resetPasswordForm,
  forgotPasswordForm,
  loginForm,
  setActiveView,
  setAdminClients,
  setAdminDevelopers,
  setForgotPasswordForm,
  setLoading,
  setLoginForm,
  setMode,
  setNotifications,
  setResetPasswordForm,
  setSelectedClientId,
  setSelectedTicket,
  setTickets,
  setToken,
  setUnreadCount,
  setUser,
  toast,
  updateSelectedTicketId,
}) {
  const handleAuthSuccess = async (response) => {
    const nextToken = response.data.access_token;
    localStorage.setItem(TOKEN_KEY, nextToken);
    setToken(nextToken);
    setUser(response.data.user);
    setActiveView("tickets");
    setSelectedClientId("");
    updateSelectedTicketId("");
    setSelectedTicket(null);
    setTickets([]);
    setAdminClients([]);
    setAdminDevelopers([]);
    toast({
      title: "Connexion reussie",
      description: "Votre espace support est disponible.",
    });
    await fetchDashboard(nextToken);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await login(loginForm);
      await handleAuthSuccess(response);
      setLoginForm(initialLogin);
    } catch (error) {
      toast({
        title: "Connexion impossible",
        description: getApiErrorMessage(error, "Identifiants invalides."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await requestPasswordReset(forgotPasswordForm);
      toast({
        title: "Reinitialisation envoyee",
        description: response.data.message,
      });
      setForgotPasswordForm(initialForgotPassword);
      setMode("login");
    } catch (error) {
      toast({
        title: "Demande impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (resetPasswordForm.new_password !== resetPasswordForm.confirm_password) {
      toast({
        title: "Confirmation incorrecte",
        description: "Les deux mots de passe doivent etre identiques.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const response = await resetPassword({
        token: resetPasswordForm.token,
        new_password: resetPasswordForm.new_password,
      });
      toast({
        title: "Mot de passe reinitialise",
        description: response.data.message,
      });
      setResetPasswordForm(initialResetPassword);
      setMode("login");
      window.history.replaceState({}, document.title, "/espace-client");
    } catch (error) {
      const isLegacyBackendContractError =
        error?.response?.status === 422 && hasValidationErrorForField(error, "email");

      toast({
        title: "Reinitialisation impossible",
        description: isLegacyBackendContractError
          ? "Le frontend utilise le nouveau lien de reinitialisation sans email, mais l'API de production attend encore l'ancien format. Il faut redeployer le backend Railway."
          : getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    handleForgotPassword,
    handleLogin,
    handleResetPassword,
  };
}
